import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  resetOnboardingInfo,
  updateOnboardingInfo,
  submitOnboardingInfo,
} from "../../redux/attorneyOnboardingSlice";
import BackIcon from "./assets/backIcon.png";
import "./profilePhoto.css";
import FileUploader from "../../components/input/new/fileUploader";
import PhotoEditor from "../../components/input/new/photoEditor";
import StepFooter from "./stepFooter";
import { supabase } from "../../supabaseClient"; // Your Supabase client
import { v4 as uuidv4 } from "uuid";

const ProfilePhoto = ({ folderType = "lawyer-profile-photos" }) => {
  const userId = useSelector(
    (state) => state.attorneyData.attorneyData?.user_id
  );
  const firmId = useSelector(
    (state) => state.attorneyData.attorneyData?.firm_id
  );
  const onboardingState = useSelector((state) => state.attorneyOnboarding);

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [croppedImage, setCroppedImage] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const goBackToProfessionals = () => {
    dispatch(resetOnboardingInfo());
    navigate(`/attorney/${userId}/home`);
  };

  const handleFilesSelected = (files) => {
    setUploadedFiles(files);
  };

  const handleUploadNew = () => {
    setUploadedFiles([]);
  };

  const uploadToSupabase = async (file) => {
    try {
      const fileExtension = file.name.split(".").pop();
      const fileName = `${uuidv4()}.${fileExtension}`;
      const folderPath = `${folderType}/${fileName}`;

      const { data, error } = await supabase.storage
        .from("photos")
        .upload(folderPath, file);

      if (error) {
        console.error("Upload error:", error.message);
        throw new Error("Failed to upload. Please try again.");
      }

      const { data: publicUrlData, error: publicUrlError } = supabase.storage
        .from("photos")
        .getPublicUrl(folderPath);

      if (publicUrlError) throw publicUrlError;

      return publicUrlData.publicUrl;
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  };

  const handleNext = async () => {
    if (!croppedImage) {
      console.error("No edited image available.");
      return;
    }
    setIsUploading(true);
    try {
      const base64Image = await fetch(croppedImage).then((res) => res.blob());
      const file = new File([base64Image], "cropped_image.jpg", {
        type: "image/jpeg",
      });
      const publicUrl = await uploadToSupabase(file);

      // Update Redux (optional, for UI consistency)
      dispatch(updateOnboardingInfo({ profilePhotoURL: publicUrl }));

      // Pass the correct profilePhotoURL directly
      dispatch(
        submitOnboardingInfo({
          onboardingData: { ...onboardingState, profilePhotoURL: publicUrl },
          userId: userId,
          firmId: firmId,
        })
      )
        .unwrap()
        .then((response) => {
          console.log("Submission successful:", response);
        })
        .catch((error) => {
          console.error("Submission failed:", error);
        });
    } catch (error) {
      console.error("Error uploading cropped file:", error);
    } finally {
      setIsUploading(false);
    }
  };

  // Disable the StepFooter's Continue button if no cropped image is available or if uploading is in progress.
  const isFooterDisabled = !croppedImage || isUploading;

  return (
    <div className="profile-photo-container">
      <div className="category-profile-types">
        <div style={{ display: "flex", flexDirection: "column", width: "50%" }}>
          <div className="category-breadcrumb" onClick={goBackToProfessionals}>
            <img
              src={BackIcon}
              alt="Back Icon"
              style={{ width: "24px", height: "24px" }}
            />
            <a className="category-back-link">Back to professionals</a>
          </div>
          <h1 className="profile-photo-title">Upload a profile photo</h1>
          <p className="profile-photo-subtitle">
            Choose a high-quality, in-focus headshot for your profile.
          </p>
          {uploadedFiles.length === 0 ? (
            <FileUploader
              accept="image/*"
              multiple={false}
              onFilesSelected={handleFilesSelected}
            />
          ) : (
            <PhotoEditor
              file={uploadedFiles[0]}
              onUploadNew={handleUploadNew}
              onImageEdited={setCroppedImage}
            />
          )}
        </div>
      </div>
      <StepFooter
        currentStep={9}
        totalSteps={9}
        location={`/attorney/${userId}/home`}
        onNext={handleNext}
        btnNextLabel={isUploading ? "Uploading..." : "Submit profile"}
        disabled={isFooterDisabled}
      />
    </div>
  );
};

export default ProfilePhoto;
